'use client';

import {
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@up2stay/up2promo-ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { PROFILE_EDIT_SPLIT_GRID_CLASS } from '@/core/components/layout';
import { triggerSelectionHaptic } from '@/core/lib/dnd-haptics';
import { cn } from '@/core/lib/utils';
import { useDictionary } from '@/core/providers/dictionary-provider';

import {
  nextScrollLeftAfterNudge,
  nextScrollLeftAfterWheel,
  scrollLeftToRevealTab,
  tabListCanScrollLeft,
  tabListCanScrollRight,
} from './tabs-overflow';

interface AnimatedUnderlineTabsProps {
  tabs: Array<{
    name: ReactNode;
    value: string;
    content: ReactNode;
  }>;
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  /** When set, the tab list is rendered full-width above a grid; tab content in left column, this node in right column (e.g. preview). */
  rightColumn?: ReactNode;
}

export function AnimatedUnderlineTabs({
  tabs,
  defaultValue,
  value: controlledValue,
  onValueChange,
  className,
  rightColumn,
}: AnimatedUnderlineTabsProps) {
  const [internalValue, setInternalValue] = useState(
    defaultValue || tabs[0]?.value || '',
  );
  const tabsRef = useRef(tabs);
  const preservedScrollTopRef = useRef<number | null>(null);
  const lastPointerTypeRef = useRef<string | null>(null);

  const dict = useDictionary();
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const tabsListRef = useRef<HTMLDivElement>(null);
  const [underlineStyle, setUnderlineStyle] = useState({ left: 0, width: 0 });
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const activeTab =
    controlledValue !== undefined ? controlledValue : internalValue;

  const getNearestIonContent = useCallback((): {
    scrollToPoint?: (x: number, y: number, duration: number) => Promise<void>;
  } | null => {
    const tabsScroller = scrollRef.current;
    if (!tabsScroller) return null;
    const ionContent = tabsScroller.closest('ion-content');
    if (!ionContent) return null;
    return ionContent as unknown as {
      scrollToPoint?: (x: number, y: number, duration: number) => Promise<void>;
    };
  }, []);

  const getNearestScrollContainer = useCallback((): HTMLElement | null => {
    const tabsScroller = scrollRef.current;
    if (!tabsScroller) return null;

    const ionContent = tabsScroller.closest('ion-content');
    if (ionContent?.shadowRoot) {
      const innerScroll =
        ionContent.shadowRoot.querySelector<HTMLElement>('.inner-scroll');
      if (innerScroll) return innerScroll;
    }

    if (typeof document === 'undefined') return null;
    return (document.scrollingElement as HTMLElement | null) ?? document.body;
  }, []);

  const restoreVerticalScroll = useCallback(
    (top: number) => {
      const ionContent = getNearestIonContent();
      if (ionContent?.scrollToPoint) {
        void ionContent.scrollToPoint(0, top, 0);
      }
      const scrollContainer = getNearestScrollContainer();
      if (!scrollContainer) return;
      if (Math.abs(scrollContainer.scrollTop - top) > 1) {
        scrollContainer.scrollTop = top;
      }
    },
    [getNearestIonContent, getNearestScrollContainer],
  );

  const handleValueChange = useCallback(
    (newValue: string) => {
      const scrollContainer = getNearestScrollContainer();
      preservedScrollTopRef.current = scrollContainer?.scrollTop ?? null;
      void triggerSelectionHaptic();
      if (controlledValue === undefined) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [controlledValue, getNearestScrollContainer, onValueChange],
  );

  const handleTriggerPointerUp = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      // On iOS Safari, keeping focus on a touched tab trigger can cause
      // IonContent to jump back to the tab row after the tab content rerenders.
      if (event.pointerType === 'touch') {
        event.currentTarget.blur();
      }
    },
    [],
  );

  const handleTriggerPointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      lastPointerTypeRef.current = event.pointerType;
      if (event.pointerType === 'touch') {
        const scrollContainer = getNearestScrollContainer();
        preservedScrollTopRef.current = scrollContainer?.scrollTop ?? null;
      }
    },
    [getNearestScrollContainer],
  );

  const handleTriggerFocus = useCallback(
    (event: React.FocusEvent<HTMLButtonElement>) => {
      if (lastPointerTypeRef.current !== 'touch') return;
      event.currentTarget.blur();
      const preservedTop = preservedScrollTopRef.current;
      if (preservedTop == null) return;
      requestAnimationFrame(() => {
        restoreVerticalScroll(preservedTop);
      });
    },
    [restoreVerticalScroll],
  );

  const refCallbacks = useMemo(() => {
    const callbacks: Array<(el: HTMLButtonElement | null) => void> = [];
    for (let i = 0; i < tabs.length; i++) {
      const index = i;
      callbacks[index] = (el: HTMLButtonElement | null) => {
        // Only update if the ref actually changed to prevent re-renders
        if (tabRefs.current[index] !== el) {
          tabRefs.current[index] = el;
        }
      };
    }
    return callbacks;
  }, [tabs.length]);

  const tabsStableKey = useMemo(
    () =>
      tabs
        .map((t) => {
          const label =
            typeof t.name === 'string' || typeof t.name === 'number'
              ? String(t.name)
              : t.value;
          return `${t.value}:${label}`;
        })
        .join('|'),
    [tabs],
  );

  useLayoutEffect(() => {
    tabsRef.current = tabs;
  }, [tabs]);

  const updateUnderline = useMemo(() => {
    return () => {
      const tabsList = tabsRef.current;
      const activeIndex = tabsList.findIndex((tab) => tab.value === activeTab);
      const activeTabElement = tabRefs.current[activeIndex];

      if (activeTabElement) {
        const { offsetLeft, offsetWidth } = activeTabElement;

        setUnderlineStyle((prev) => {
          if (prev.left === offsetLeft && prev.width === offsetWidth) {
            return prev;
          }
          return {
            left: offsetLeft,
            width: offsetWidth,
          };
        });
      }
    };
  }, [activeTab]);

  const updateScrollHint = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(tabListCanScrollLeft(scrollLeft));
    setCanScrollRight(
      tabListCanScrollRight(scrollLeft, scrollWidth, clientWidth),
    );
  }, []);

  useLayoutEffect(() => {
    let cancelled = false;
    let frame2: number | null = null;

    // Run after layout: double rAF so we run after the next paint (refs and dimensions are stable)
    const frame1 = requestAnimationFrame(() => {
      frame2 = requestAnimationFrame(() => {
        if (!cancelled) updateUnderline();
      });
    });

    // Fallback: run again after a short delay so underline appears even if first paint was late (e.g. coming from loading state)
    const timeout = setTimeout(updateUnderline, 100);
    const timeoutHint = setTimeout(updateScrollHint, 150);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame1);
      if (frame2 !== null) cancelAnimationFrame(frame2);
      clearTimeout(timeout);
      clearTimeout(timeoutHint);
    };
  }, [updateUnderline, updateScrollHint, tabsStableKey, tabs]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    const list = tabsListRef.current;
    if (!el) return;
    const id = requestAnimationFrame(updateScrollHint);
    const ro = new ResizeObserver(updateScrollHint);
    ro.observe(el);
    if (list) ro.observe(list);
    return () => {
      cancelAnimationFrame(id);
      ro.disconnect();
    };
  }, [updateScrollHint, tabsStableKey]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    const activeIndex = tabsRef.current.findIndex(
      (tab) => tab.value === activeTab,
    );
    const activeEl = activeIndex >= 0 ? tabRefs.current[activeIndex] : null;
    if (!el || !activeEl) return;
    const targetScrollLeft = scrollLeftToRevealTab({
      tabOffsetLeft: activeEl.offsetLeft,
      tabOffsetWidth: activeEl.offsetWidth,
      scrollLeft: el.scrollLeft,
      clientWidth: el.clientWidth,
    });
    if (el.scrollLeft !== targetScrollLeft) {
      const reduceMotion =
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollTo({
        left: targetScrollLeft,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }
    const timeout = setTimeout(updateScrollHint, 350);
    return () => clearTimeout(timeout);
  }, [activeTab, tabsStableKey, updateScrollHint]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      const result = nextScrollLeftAfterWheel({
        scrollLeft: el.scrollLeft,
        scrollWidth: el.scrollWidth,
        clientWidth: el.clientWidth,
        deltaX: event.deltaX,
        deltaY: event.deltaY,
      });
      if (!result.consumed) return;
      el.scrollLeft = result.scrollLeft;
      event.preventDefault();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [tabsStableKey]);

  const nudgeTabs = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollTo({
      left: nextScrollLeftAfterNudge({
        scrollLeft: el.scrollLeft,
        scrollWidth: el.scrollWidth,
        clientWidth: el.clientWidth,
        direction,
      }),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }, []);

  useLayoutEffect(() => {
    const preservedTop = preservedScrollTopRef.current;
    if (preservedTop == null) return;
    preservedScrollTopRef.current = null;

    const restore = () => {
      restoreVerticalScroll(preservedTop);
    };

    const frame = requestAnimationFrame(() => {
      restore();
      // iOS/Safari can apply focus scrolling slightly later.
      setTimeout(restore, 60);
      setTimeout(restore, 160);
    });

    return () => cancelAnimationFrame(frame);
  }, [activeTab, restoreVerticalScroll]);

  const listElement = (
    <div className="relative min-w-0 w-full max-w-full">
      <div
        ref={scrollRef}
        className="min-w-0 w-full max-w-full overflow-x-auto overflow-y-hidden overscroll-x-contain touch-pan-x [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ WebkitOverflowScrolling: 'touch' }}
        onScroll={updateScrollHint}
      >
        <TabsList
          ref={tabsListRef}
          className="relative inline-flex w-max max-w-none rounded-none border-b bg-transparent p-0"
        >
          {tabs.map((tab, index) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              ref={refCallbacks[index]}
              onPointerDown={handleTriggerPointerDown}
              onPointerUp={handleTriggerPointerUp}
              onFocus={handleTriggerFocus}
              className="relative z-10 shrink-0 touch-pan-x rounded-none border-0 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none dark:data-[state=active]:bg-transparent"
            >
              {tab.name}
            </TabsTrigger>
          ))}

          <div
            className="bg-primary pointer-events-none absolute bottom-0 z-20 h-0.5 transition-[left,width] duration-200 ease-out"
            style={{
              left: underlineStyle.left,
              width: underlineStyle.width,
            }}
          />
        </TabsList>
      </div>
      {canScrollLeft && (
        <div
          className="pointer-events-none absolute bottom-0 left-0 top-0 w-10 bg-gradient-to-r from-[var(--theme-background)] to-transparent"
          aria-hidden
        />
      )}
      {canScrollRight && (
        <div
          className="pointer-events-none absolute bottom-0 right-0 top-0 w-10 bg-gradient-to-l from-[var(--theme-background)] to-transparent"
          aria-hidden
        />
      )}
      {canScrollLeft && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute top-1/2 left-0 z-30 size-9 -translate-y-1/2 bg-[var(--theme-background)]/90 text-foreground shadow-sm"
          aria-label={dict.ui.tabs.scrollPrevious}
          onClick={() => nudgeTabs('left')}
        >
          <ChevronLeft />
        </Button>
      )}
      {canScrollRight && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute top-1/2 right-0 z-30 size-9 -translate-y-1/2 bg-[var(--theme-background)]/90 text-foreground shadow-sm"
          aria-label={dict.ui.tabs.scrollNext}
          onClick={() => nudgeTabs('right')}
        >
          <ChevronRight />
        </Button>
      )}
    </div>
  );

  return (
    <div
      className={cn('min-w-0 max-w-full flex flex-col flex-1 gap-4', className)}
    >
      <Tabs
        value={activeTab}
        onValueChange={handleValueChange}
        className={cn(
          'flex min-w-0 max-w-full flex-col gap-4',
          rightColumn != null && 'flex-1 min-h-0',
          className,
        )}
      >
        <div className="min-w-0 w-full max-w-full">{listElement}</div>

        {rightColumn != null ? (
          <div className={PROFILE_EDIT_SPLIT_GRID_CLASS}>
            <div className="min-w-0 flex flex-1 flex-col min-h-0 overflow-x-hidden">
              {tabs.map((tab) => (
                <TabsContent
                  key={tab.value}
                  value={tab.value}
                  className="mt-0 h-full data-[state=inactive]:hidden"
                >
                  {tab.content}
                </TabsContent>
              ))}
            </div>
            <div className="hidden md:contents">{rightColumn}</div>
          </div>
        ) : (
          <div className="min-h-0 flex-1 flex flex-col min-w-0">
            {tabs.map((tab) => (
              <TabsContent
                key={tab.value}
                value={tab.value}
                className="mt-0 data-[state=inactive]:hidden"
              >
                {tab.content}
              </TabsContent>
            ))}
          </div>
        )}
      </Tabs>
    </div>
  );
}
